import { useContext, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { RegulationContext } from '../../App';
import { FINALIZE_CONFIG_POLLING_INTERVAL } from '../constants/common';
import StandardService from '../service/StandardService';

/**
 * Custom hook to manage finalize status polling for standards-related routes
 * Polling continues as long as user is on any of these routes:
 * - /standard (StandardsMain)
 * - /topiclist/:std_id (Topics)
 * - /metriclist/:std_id/:topic_id (Metric)
 * - /datapointlist/:std_id/:topic_id/:metric_id (DataPoints)
 * - /qualquestions/:std_id/:topic_id/:metric_id (QualitativeQuestionsView)
 * 
 * @returns {boolean} isFinalizeInProgress - Whether finalize is currently in progress
 * @returns {function} triggerPolling - Function to manually trigger polling (e.g., after finalize confirmation)
 */
const useFinalizeStatusPoller = () => {
  const location = useLocation();
  const { glbRegulation } = useContext(RegulationContext);
  const [isFinalizeInProgress, setIsFinalizeInProgress] = useState(false);
  const finalizeStatusPollerRef = useRef(null);
  const onFormCreationCompleteRef = useRef(null);
  const lsData = JSON.parse(localStorage.getItem("authority"));

  // Define routes that should have polling active
  const STANDARDS_ROUTES = [
    '/standard',
    '/topiclist/',
    '/metriclist/',
    '/datapointlist/',
    '/qualquestions/'
  ];

  // Check if current route is a standards-related route
  const isStandardsRoute = () => {
    const pathname = location.pathname;
    return STANDARDS_ROUTES.some(route => pathname.includes(route));
  };

  // Check finalize status
  const checkFinalizeStatus = () => {
    if (!lsData?.profile?.company?.id || !glbRegulation?.id) {
      return;
    }
    StandardService.getFormCreationStatus(lsData.profile.company.id, glbRegulation.id)
      .then((response) => {
        if (response.status === 200) {
          const isInProgress = response.data === true || response.data === "true";
          setIsFinalizeInProgress(isInProgress);
          if (!isInProgress) {
            if (onFormCreationCompleteRef.current) {
              onFormCreationCompleteRef.current();
              onFormCreationCompleteRef.current = null;
            }
            stopFinalizeStatusPoller();
          }
        }
      })
      .catch((error) => {
        stopFinalizeStatusPoller();
      });
  };

  // Start polling for finalize status
  const startFinalizeStatusPoller = () => {
    stopFinalizeStatusPoller();
    checkFinalizeStatus();
    finalizeStatusPollerRef.current = setTimeout(function request() {
      checkFinalizeStatus();
      // Only continue polling if finalize is still in progress and we're still on a standards route
      if (finalizeStatusPollerRef.current && isStandardsRoute()) {
        finalizeStatusPollerRef.current = setTimeout(request, FINALIZE_CONFIG_POLLING_INTERVAL);
      }
    }, FINALIZE_CONFIG_POLLING_INTERVAL);
  };

  // Stop polling for finalize status
  const stopFinalizeStatusPoller = () => {
    if (finalizeStatusPollerRef.current) {
      clearTimeout(finalizeStatusPollerRef.current);
      finalizeStatusPollerRef.current = null;
    }
  };

  /**
   * Trigger polling manually (e.g., after finalize confirmation).
   * @param {function} [onFormCreationComplete] - Callback invoked when form-creation/status API returns success and form creation is complete (no longer in progress).
   */
  const triggerPolling = (onFormCreationComplete) => {
    if (isStandardsRoute() && lsData?.profile?.company?.id && glbRegulation?.id) {
      onFormCreationCompleteRef.current = onFormCreationComplete || null;
      setIsFinalizeInProgress(true);
      startFinalizeStatusPoller();
    }
  };

  // Effect to manage polling based on route and regulation/company changes
  useEffect(() => {
    // Only poll if we're on a standards route
    if (!isStandardsRoute()) {
      stopFinalizeStatusPoller();
      setIsFinalizeInProgress(false);
      return;
    }

    // Check if we have required data
    if (!lsData?.profile?.company?.id || !glbRegulation?.id) {
      return;
    }

    // Initial check on mount or when route/regulation/company changes
    StandardService.getFormCreationStatus(lsData.profile.company.id, glbRegulation.id)
      .then((response) => {
        if (response.status === 200) {
          const isInProgress = response.data === true || response.data === "true";
          setIsFinalizeInProgress(isInProgress);
          if (isInProgress) {
            startFinalizeStatusPoller();
          }
        }
      })
      .catch((error) => {
        // Silently handle errors
      });

    // Cleanup on unmount or route change
    return () => {
      stopFinalizeStatusPoller();
    };
  }, [location.pathname, glbRegulation?.id, lsData?.profile?.company?.id]);

  return { isFinalizeInProgress, triggerPolling };
};

export default useFinalizeStatusPoller;
